import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { api } from '../lib/api.js';
import { SignaturePad, type SignaturePadHandle } from '../components/primitives/SignaturePad.js';

interface SignDoc {
  name: string;
  url: string;
  mimeType: string;
  message: string | null;
  docType: string | null;
  docRef: string | null;
  docNotes: string | null;
  requireSignature: boolean;
  sharedBy: string | null;
  sharedAt: string;
  needsCode: boolean;
  codeTo: string | null;
  recipient: { name: string; status: 'pending' | 'viewed' | 'signed' | 'declined'; signedAt: string | null; declineReason: string | null; signedPdf: string | null; sealedAt: string | null };
}

const fmt = (iso: string | null) => iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const errorText = (e: unknown) => axios.isAxiosError(e) ? String((e.response?.data as { message?: string })?.message ?? e.message) : (e as Error).message;

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ height: '100vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 16, background: 'var(--bg)' }}>
      <div id="sign-page" style={{ width: 760, maxWidth: '100%', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: 24 }}>
        <div style={{ marginBottom: 14, textAlign: 'center' }}>
          <div className="logo-badge">CLS</div>
          <div className="logo-sub" style={{ marginTop: 6 }}>Construction Logistics Services · Document signing</div>
        </div>
        {children}
      </div>
    </div>
  );
}

export function SignPage() {
  const { token = '' } = useParams();
  const qc = useQueryClient();
  const pad = useRef<SignaturePadHandle | null>(null);
  const [confirmName, setConfirmName] = useState('');
  const [hasInk, setHasInk] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);
  const [code, setCode] = useState('');
  const [codeSentTo, setCodeSentTo] = useState<string | null>(null);

  const { data, error: loadError, isLoading } = useQuery({
    queryKey: ['sign', token],
    queryFn: () => api.get<SignDoc>(`/sign/${token}`).then((r) => r.data),
    retry: false,
  });

  useEffect(() => { document.title = data ? `Sign: ${data.name}` : 'CLS — Sign document'; }, [data]);
  useEffect(() => {
    if (data?.recipient.status === 'pending') api.post(`/sign/${token}/view`).catch(() => undefined);
  }, [data?.recipient.status, token]);

  const sign = useMutation({
    mutationFn: async () => {
      const needs = data?.requireSignature ?? true;
      const signatureData = hasInk ? pad.current?.toDataURL() ?? '' : '';
      if (!confirmName.trim()) throw new Error('Please type your full name to confirm');
      if (needs && !signatureData) throw new Error('Please draw your signature');
      if (!consent) throw new Error('Please tick the box to agree to sign electronically');
      if (data?.needsCode && !code.trim()) throw new Error('Enter the code we emailed you');
      await api.post(`/sign/${token}`, { signatureData, confirmName: confirmName.trim(), consent, code: code.trim() || undefined });
    },
    onSuccess: () => { setError(null); qc.invalidateQueries({ queryKey: ['sign', token] }); },
    onError: (e: unknown) => setError(errorText(e)),
  });

  const sendCode = useMutation({
    mutationFn: async () => (await api.post<{ sent: boolean; to: string }>(`/sign/${token}/code`)).data,
    onSuccess: (r) => { setError(r.sent ? null : 'The code could not be emailed, try again or contact CLS'); setCodeSentTo(r.sent ? r.to : null); },
    onError: (e: unknown) => setError(errorText(e)),
  });

  const decline = useMutation({
    mutationFn: async () => { await api.post(`/sign/${token}/decline`, { reason: reason.trim() || null }); },
    onSuccess: () => { setDeclining(false); setError(null); qc.invalidateQueries({ queryKey: ['sign', token] }); },
    onError: (e: unknown) => setError(errorText(e)),
  });

  if (isLoading) return <Frame><div style={{ textAlign: 'center', color: 'var(--muted)', padding: 30 }}>Loading your document…</div></Frame>;
  if (loadError || !data) {
    const status = axios.isAxiosError(loadError) ? loadError.response?.status : undefined;
    const msg = axios.isAxiosError(loadError) ? (loadError.response?.data as { message?: string })?.message : null;
    return (
      <Frame>
        <div style={{ textAlign: 'center', padding: 30 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>{status === 410 ? '⌛' : '🔗'}</div>
          <div id="sign-error" style={{ fontSize: 14, fontWeight: 600 }}>{msg ?? 'This signing link is not valid.'}</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>Contact CLS if you were expecting a document to sign.</div>
        </div>
      </Frame>
    );
  }

  const r = data.recipient;
  const needsInk = data.requireSignature;
  const isPdf = data.mimeType === 'application/pdf' || data.url.toLowerCase().endsWith('.pdf');
  const isImage = /^image\//.test(data.mimeType) || /\.(png|jpe?g|gif|webp)$/i.test(data.url);

  const form = (
    <div>
      <div className="form-row">
        <label className="form-label">Your full name (type to confirm you have read the document)</label>
        <input type="text" className="form-input" id="sign-name" placeholder={r.name} value={confirmName} onChange={(e) => setConfirmName(e.target.value)} />
      </div>
      {needsInk ? (
        <div className="form-row">
          <label className="form-label">Signature — draw below with your finger or mouse</label>
          <SignaturePad ref={pad} onInk={setHasInk} />
        </div>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>This document only needs your acknowledgement — type your name and confirm.</div>
      )}
      {data.needsCode ? (
        <div className="form-row">
          <label className="form-label">One-time code (we email it to {data.codeTo})</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="text" inputMode="numeric" autoComplete="one-time-code" className="form-input" id="sign-code" placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value)} style={{ maxWidth: 160 }} />
            <button type="button" className="btn btn-ghost" id="sign-send-code" onClick={() => sendCode.mutate()} disabled={sendCode.isPending}>{sendCode.isPending ? 'Sending…' : codeSentTo ? 'Send again' : 'Email me a code'}</button>
          </div>
          {codeSentTo ? <div id="sign-code-sent" style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Code sent to {codeSentTo}. It is valid for 15 minutes.</div> : null}
        </div>
      ) : null}
      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, marginBottom: 10, cursor: 'pointer' }}>
        <input type="checkbox" id="sign-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ marginTop: 2 }} />
        <span>I agree to sign this document electronically and I understand that my electronic signature is as binding as a handwritten one.</span>
      </label>
      {error ? <div id="sign-form-error" style={{ color: 'var(--danger)', fontSize: 12, marginBottom: 10 }}>{error}</div> : null}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="btn btn-primary" id="sign-submit" style={{ flex: 1, justifyContent: 'center', padding: 12 }} onClick={() => sign.mutate()} disabled={sign.isPending}>
          {sign.isPending ? 'Submitting…' : needsInk ? '✍ Sign & submit' : '✓ I have read and acknowledge'}
        </button>
        {!declining ? <button className="btn btn-ghost" id="sign-decline" onClick={() => setDeclining(true)}>Decline</button> : null}
      </div>
      {declining ? (
        <div style={{ marginTop: 10, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: 10 }}>
          <label className="form-label">Why are you declining? (optional)</label>
          <input type="text" className="form-input" id="sign-decline-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setDeclining(false)}>Cancel</button>
            <button className="btn btn-sm" id="sign-decline-confirm" style={{ background: 'var(--danger)', color: '#fff', border: 'none' }} onClick={() => decline.mutate()} disabled={decline.isPending}>Confirm decline</button>
          </div>
        </div>
      ) : null}
      <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 10 }}>By confirming you state that you have read, understood and agree to the contents of this document. Your name, the time, your IP address and device are recorded with it.</div>
    </div>
  );

  return (
    <Frame>
      <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 18 }} id="sign-doc-name">{data.name}</div>
      <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 12 }}>
        For {r.name} · shared by {data.sharedBy ?? 'CLS'} on {fmt(data.sharedAt)}{data.docType ? ` · ${data.docType}` : ''}{data.docRef ? ` · Ref ${data.docRef}` : ''}
      </div>
      {data.message ? <div style={{ background: 'var(--accent-dim)', border: '1px solid rgba(232,164,39,.3)', borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 12 }}><strong>Message from {data.sharedBy ?? 'CLS'}:</strong> {data.message}</div> : null}
      {data.docNotes ? <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>{data.docNotes}</div> : null}

      <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 14, overflow: 'hidden' }}>
        {isPdf ? <iframe src={data.url} title={data.name} style={{ width: '100%', height: '55vh', border: 'none', display: 'block' }} />
          : isImage ? <img src={data.url} alt={data.name} style={{ maxWidth: '100%', display: 'block' }} />
          : <div style={{ padding: 20, textAlign: 'center' }}><a className="btn btn-primary" href={data.url} target="_blank" rel="noopener noreferrer">📄 Open the document</a></div>}
      </div>
      <div style={{ marginBottom: 14 }}><a href={data.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: 'var(--accent)' }}>Open the document in a new tab ↗</a></div>

      {r.status === 'signed' ? (
        <div id="sign-done" style={{ background: 'rgba(34,197,94,.1)', border: '1px solid rgba(34,197,94,.4)', borderRadius: 10, padding: 16, textAlign: 'center' }}>
          <div style={{ fontSize: 24 }}>✓</div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Thank you, {r.name}. This document was {needsInk ? 'signed' : 'acknowledged'} on {fmt(r.signedAt)}.</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>CLS has a record of your confirmation, the time and the device used.{r.sealedAt ? ' Your signed copy is digitally sealed and timestamped.' : ''}</div>
          {r.signedPdf ? <a className="btn btn-ghost btn-sm" id="sign-download" href={r.signedPdf} style={{ marginTop: 10 }}>⬇ Download your signed copy</a> : null}
        </div>
      ) : r.status === 'declined' ? (
        <div id="sign-declined" style={{ background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.35)', borderRadius: 10, padding: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14, textAlign: 'center' }}>You declined to sign this document{r.declineReason ? `: "${r.declineReason}"` : '.'}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, marginBottom: 12, textAlign: 'center' }}>CLS has been told. Changed your mind? You can still sign below.</div>
          {form}
        </div>
      ) : form}
    </Frame>
  );
}
